<template>
  <div class="entity-overview-layout">
    <div class="entity-overview-layout__grid">
      <section class="entity-overview-layout__cell entity-overview-layout__cell--top-left">
        <slot name="top-left" />
      </section>

      <section class="entity-overview-layout__cell entity-overview-layout__cell--top-right">
        <slot name="top-right" />
      </section>

      <section
        v-if="$slots['bottom-left'] || resolvedLeftPanels.length"
        class="entity-overview-layout__cell entity-overview-layout__cell--bottom-left"
      >
        <div v-if="resolvedLeftPanels.length" class="overview-switcher">
          <div class="overview-switcher__tabs" role="tablist" aria-label="Overview side panels">
            <button
              v-for="panel in resolvedLeftPanels"
              :key="panel.id"
              type="button"
              class="overview-switcher__tab"
              :class="{ 'overview-switcher__tab--active': activeLeftPanelId === panel.id }"
              :aria-selected="activeLeftPanelId === panel.id"
              @click="activeLeftPanelId = panel.id"
            >
              {{ panel.label }}
            </button>
          </div>

          <div class="overview-switcher__panel">
            <slot :name="`left-panel-${activeLeftPanelId}`" />
          </div>
        </div>

        <slot v-else name="bottom-left" />
      </section>

      <section class="entity-overview-layout__cell entity-overview-layout__cell--bottom-right">
        <div v-if="resolvedPanels.length" class="overview-switcher">
          <div class="overview-switcher__tabs" role="tablist" aria-label="Overview panels">
            <button
              v-for="panel in resolvedPanels"
              :key="panel.id"
              type="button"
              class="overview-switcher__tab"
              :class="{ 'overview-switcher__tab--active': activePanelId === panel.id }"
              :aria-selected="activePanelId === panel.id"
              @click="activePanelId = panel.id"
            >
              {{ panel.label }}
            </button>
          </div>

          <div class="overview-switcher__panel">
            <slot :name="`panel-${activePanelId}`" />
          </div>
        </div>

        <slot v-else name="bottom-right" />
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EntityOverviewLayout',
  props: {
    leftPanelTabs: {
      type: Array,
      default: () => [],
    },
    defaultLeftPanel: {
      type: String,
      default: '',
    },
    panelTabs: {
      type: Array,
      default: () => [],
    },
    defaultPanel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      activeLeftPanelId: '',
      activePanelId: '',
    };
  },
  computed: {
    resolvedLeftPanels() {
      return (this.leftPanelTabs || []).filter((panel) => panel?.id && panel?.label);
    },
    resolvedPanels() {
      return (this.panelTabs || []).filter((panel) => panel?.id && panel?.label);
    },
  },
  watch: {
    resolvedLeftPanels: {
      immediate: true,
      handler(nextPanels) {
        const nextDefault = this.defaultLeftPanel && nextPanels.some((panel) => panel.id === this.defaultLeftPanel)
          ? this.defaultLeftPanel
          : nextPanels[0]?.id || '';

        if (!nextPanels.some((panel) => panel.id === this.activeLeftPanelId)) {
          this.activeLeftPanelId = nextDefault;
        }
      },
    },
    resolvedPanels: {
      immediate: true,
      handler(nextPanels) {
        const nextDefault = this.defaultPanel && nextPanels.some((panel) => panel.id === this.defaultPanel)
          ? this.defaultPanel
          : nextPanels[0]?.id || '';

        if (!nextPanels.some((panel) => panel.id === this.activePanelId)) {
          this.activePanelId = nextDefault;
        }
      },
    },
    defaultLeftPanel(nextDefault) {
      if (nextDefault && this.resolvedLeftPanels.some((panel) => panel.id === nextDefault)) {
        this.activeLeftPanelId = nextDefault;
      }
    },
    defaultPanel(nextDefault) {
      if (nextDefault && this.resolvedPanels.some((panel) => panel.id === nextDefault)) {
        this.activePanelId = nextDefault;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.entity-overview-layout {
  width: 100%;
  max-width: 1480px;
  margin: 0 auto;
  padding: 6px 4px 18px;
}

.entity-overview-layout__grid {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(360px, 0.92fr);
  gap: 22px;
  align-items: stretch;
}

.entity-overview-layout__cell {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.entity-overview-layout__cell--bottom-left,
.entity-overview-layout__cell--bottom-right {
  align-self: stretch;
}

.entity-overview-layout__cell--top-left > *,
.entity-overview-layout__cell--top-right > *,
.entity-overview-layout__cell--bottom-left > *,
.entity-overview-layout__cell--bottom-right > * {
  flex: 1 1 auto;
}

.overview-switcher {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 100%;
}

.overview-switcher__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 8px;
  background: linear-gradient(180deg, rgba($secondary-color, 0.74), rgba(#fff, 0.94));
  border: 1px solid rgba($primary-color, 0.08);
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.05);
}

.overview-switcher__tab {
  border: 1px solid rgba($primary-color, 0.18);
  background: rgba(#fff, 0.92);
  color: $primary-color;
  border-radius: 999px;
  padding: 8px 14px;
  font-weight: 600;
  font-size: 0.94rem;
  line-height: 1.1;
  cursor: pointer;
  transition: all 0.18s ease;
}

.overview-switcher__tab:hover {
  background: rgba($secondary-color, 0.98);
  transform: translateY(-1px);
}

.overview-switcher__tab--active {
  background: $primary-color;
  color: #fff;
  border-color: $primary-color;
  box-shadow: 0 10px 20px rgba($primary-color, 0.18);
}

.overview-switcher__panel {
  min-width: 0;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
}

.entity-overview-layout :deep(.instrument-details),
.entity-overview-layout :deep(.location-details),
.entity-overview-layout :deep(.material-details),
.entity-overview-layout :deep(.record-details),
.entity-overview-layout :deep(.project-details),
.entity-overview-layout :deep(.experiment-details),
.entity-overview-layout :deep(.element-details),
.entity-overview-layout :deep(.experiment-files),
.entity-overview-layout :deep(.experiment-comments),
.entity-overview-layout :deep(.materials),
.entity-overview-layout :deep(.workflow-list),
.entity-overview-layout :deep(.experiment-list),
.entity-overview-layout :deep(.instruments) {
  max-width: none;
  margin: 0 !important;
  border-radius: 18px;
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba($primary-color, 0.08);
}

.entity-overview-layout :deep(.experiment-files),
.entity-overview-layout :deep(.experiment-comments),
.entity-overview-layout :deep(.materials),
.entity-overview-layout :deep(.workflow-list),
.entity-overview-layout :deep(.experiment-list),
.entity-overview-layout :deep(.instruments) {
  margin-top: 0;
  padding: 18px !important;
}

.entity-overview-layout :deep(.header-row),
.entity-overview-layout :deep(.header-container),
.entity-overview-layout :deep(.files-header) {
  margin-bottom: 16px !important;
  padding-right: 0 !important;
}

.entity-overview-layout :deep(.title-wrap) {
  gap: 10px;
}

.entity-overview-layout :deep(.meta-count) {
  font-size: 0.87rem;
  font-weight: 600;
  color: rgba($primary-color, 0.68);
}

.entity-overview-layout :deep(.search-filter-row) {
  margin: 0 0 12px !important;
}

.entity-overview-layout :deep(.pagination) {
  margin-top: 16px !important;
}

.entity-overview-layout :deep(.files-container) {
  min-height: 430px;
}

.entity-overview-layout :deep(.folder-section),
.entity-overview-layout :deep(.files-table-section) {
  min-height: 430px;
}

.entity-overview-layout :deep(.display-image-gallery) {
  height: 100%;
}

.entity-overview-layout :deep(.header h2),
.entity-overview-layout :deep(.header-content h2),
.entity-overview-layout :deep(.material-title) {
  margin-bottom: 0 !important;
}

.entity-overview-layout :deep(.material-path),
.entity-overview-layout :deep(.experiment-path) {
  margin-bottom: 8px;
}

.entity-overview-layout :deep(table) {
  width: 100%;
  max-width: none;
  margin: 0;
}

.entity-overview-layout :deep(th),
.entity-overview-layout :deep(td) {
  padding: 11px 12px;
}

.entity-overview-layout :deep(th) {
  background: rgba($primary-color, 0.06);
}

.entity-overview-layout :deep(.add-material-controls),
.entity-overview-layout :deep(.add-instrument-controls) {
  margin-top: auto;
  padding-top: 14px;
}

.entity-overview-layout :deep(.empty),
.entity-overview-layout :deep(.empty-state) {
  color: rgba($primary-color, 0.62);
}

@media (max-width: 1200px) {
  .entity-overview-layout__grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 18px;
  }

  .entity-overview-layout {
    padding-left: 0;
    padding-right: 0;
  }

  .overview-switcher__tabs {
    padding: 6px;
  }
}
</style>
